import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "@/components/ui/button";
import { handleGmailAuth } from "@/utils/gmail/authUtils";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

interface GmailAuthProps {
  onSuccess: (email: string, credentials: any) => void;
  accountName?: string;
}

const GmailAuth = ({ onSuccess, accountName }: GmailAuthProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const login = useGoogleLogin({
    scope: 'https://mail.google.com/ https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
    onSuccess: async (response) => {
      try {
        console.log('Google OAuth success, fetching user info...', response);
        // Get user info
        const userResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            'Authorization': `Bearer ${response.access_token}`,
          },
        });
        
        if (!userResponse.ok) {
          console.error('Failed to get user info:', await userResponse.text());
          throw new Error('Failed to get user info');
        }

        const userData = await userResponse.json();
        console.log('Gmail user data:', userData);

        // Invalidate queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });

        const result = await handleGmailAuth(
          { email: userData.email, name: userData.name },
          response.access_token,
          response.expires_in,
          accountName
        );
        
        console.log('Gmail auth completed successfully:', result);
        onSuccess(result.email, result.credentials);
        
        toast({
          title: "Gmail Connected",
          description: "Your Gmail account has been connected successfully.",
        });
      } catch (error) {
        console.error('Error in Gmail auth:', error);
        toast({
          title: "Connection Failed",
          description: "Failed to connect Gmail account. Please try again.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error('Google OAuth error:', error);
      toast({
        title: "Authentication Failed",
        description: "Failed to authenticate with Google. Please try again.",
        variant: "destructive",
      });
    },
    flow: 'implicit'
  });

  return (
    <Button 
      type="button" 
      onClick={() => login()} 
      className="w-full"
    >
      Connect Gmail Account
    </Button>
  );
};

export default GmailAuth;